import { createContext, useContext, useEffect, useState } from 'react';
import { leerTema, guardarTema, aplicarTemaEnDocumento, TEMA_DEFAULT } from '../lib/tema';
import { temaDelDia } from '../lib/dieciocho';
import { leerTexto, guardarTexto, aplicarTextoEnDocumento, TEXTO_DEFAULT } from '../lib/texto';
import { leerLetra, guardarLetra, aplicarLetraEnDocumento, LETRA_DEFAULT } from '../lib/letra';

const ThemeContext = createContext({
  dark: false, toggle: () => {},
  tema: TEMA_DEFAULT, setTema: () => {},
  texto: TEXTO_DEFAULT, setTexto: () => {},
  letra: LETRA_DEFAULT, setLetra: () => {},
  adoptarApariencia: () => {},
});

export function ThemeProvider({ children }) {
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem('mipae_theme');
    if (stored) return stored === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [tema, setTemaState] = useState(() => leerTema());
  const [texto, setTextoState] = useState(() => leerTexto());
  const [letra, setLetraState] = useState(() => leerLetra());

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
      localStorage.setItem('mipae_theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('mipae_theme', 'light');
    }
  }, [dark]);

  // Tema de ambiente elegible por el usuario: variables inline en <html> que
  // ganan a .dark/:root sin tocar index.css (aplicarTemaEnDocumento también
  // sincroniza el meta theme-color, incluido el caso default). Depende
  // también de `dark` porque cada tema tiene su propia versión clara/oscura.
  useEffect(() => {
    // Del 17 al 19 de septiembre el ambiente del Dieciocho entra SOLO para
    // quien nunca eligió tema (ver temaDelDia). `tema` no cambia, así que el
    // selector sigue mostrando la elección real y nada se pierde al pasar la
    // fecha: fuera de la ventana temaDelDia devuelve el tema tal cual.
    aplicarTemaEnDocumento(temaDelDia(tema), { oscuro: dark });
  }, [dark, tema]);

  // Tamaño de texto elegible por el usuario: variable inline en <html> que
  // multiplica los fontSize de Tailwind (ver src/lib/texto.js). No depende
  // de `dark`: la escala es la misma en ambos modos.
  useEffect(() => {
    aplicarTextoEnDocumento(texto);
  }, [texto]);

  // Tipo de letra elegible por el usuario: mismo patrón que tema/texto. No
  // depende de `dark`: la familia es la misma en ambos modos.
  useEffect(() => {
    aplicarLetraEnDocumento(letra);
  }, [letra]);

  const toggle = () => setDark(d => !d);
  const setTema = (id) => setTemaState(guardarTema(id));
  const setTexto = (id) => setTextoState(guardarTexto(id));
  const setLetra = (id) => setLetraState(guardarLetra(id));

  // Apariencia de la CUENTA (2026-09-10): al iniciar sesión, App trae lo
  // guardado en profiles.apariencia y lo adopta aquí de una vez; cada guardar*
  // espeja en localStorage, así el próximo arranque del dispositivo ya lo
  // lee antes de montar. `modo` null = respetar lo que ya tenía el aparato.
  const adoptarApariencia = ({ tema: t, letra: l, texto: x, modo } = {}) => {
    if (t) setTemaState(guardarTema(t));
    if (l) setLetraState(guardarLetra(l));
    if (x) setTextoState(guardarTexto(x));
    if (modo === 'claro' || modo === 'oscuro') setDark(modo === 'oscuro');
  };

  return (
    <ThemeContext.Provider value={{ dark, toggle, tema, setTema, texto, setTexto, letra, setLetra, adoptarApariencia }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
